import { ChevronDown, LogIn, Mail, Menu, Phone, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '@/nucleo/auth/useAuth';
import { destinoDoUsuario } from '@/nucleo/auth/destino';
import { type UsuarioAutenticado } from '@/nucleo/auth/tipos';
import { cn } from '@/nucleo/utilitarios';
import { AcessibilidadeWidget } from '@/ui/AcessibilidadeWidget';
import { Button } from '@/ui/base/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/ui/base/dropdown-menu';
import { CookieConsent } from '@/ui/CookieConsent';
import { SkipToContent } from '@/ui/SkipToContent';
import { TegLogo } from '@/ui/TegLogo';
import { ThemeToggle } from '@/ui/ThemeToggle';
import logoCidade from '@/ui/assets/logo-cidade-sp-branco.png';

import { MenuDoInscrito } from './credenciamento/AcompanhamentoSessao';
import { useSessaoDoInscrito } from './credenciamento/identidade/api';
import {
  CABECALHO_PUBLICO,
  RODAPE_PUBLICO,
  SECOES_DO_RODAPE,
  type ItemPublico,
} from './navegacaoPublica';

/**
 * Casca do portal público: cabeçalho institucional, conteúdo e rodapé.
 *
 * Monta as três exigências que o Prompt 6 pede na raiz — aviso de privacidade
 * (link no rodapé e na faixa de cookies), aviso de cookies e widget de
 * acessibilidade — e o "pular para o conteúdo" como primeiro elemento focável
 * (docs/LGPD-E-ACESSIBILIDADE.md, seções 8 e 11).
 *
 * O contato do encarregado de dados é publicado no rodapé, como manda a seção 5
 * do mesmo documento. O endereço vem de `VITE_CONTATO_ENCARREGADO`: é dado
 * institucional, não código, e inventar um endereço num portal de governo seria
 * pior que omitir. Sem a variável, o rodapé afirma o direito e diz que o canal
 * é publicado pela Secretaria — nunca um endereço fabricado.
 *
 * **Rodapé em quatro colunas, decisão do dono em 2026-09-15.** Era um bloco de
 * dois parágrafos com os nove links numa fileira plana; passa a ser a grade do
 * protótipo — marca, Serviços, Institucional, Contato e LGPD — sobre a mesma
 * faixa azul do cabeçalho.
 *
 * A faixa usa `bg-sidebar`, e não o `bg-foreground` do protótipo, por dois
 * motivos: é o par que o cabeçalho já usa, então os dois fecham a página como um
 * conjunto; e `sidebar-foreground/sidebar-background` já é conferido pela
 * auditoria de contraste nos três temas, enquanto `foreground` inverte no tema
 * de alto contraste e deixaria o rodapé amarelo sobre amarelo. Pela mesma razão
 * os links deixam de usar `text-link`: a auditoria confere `link` sobre
 * `background` e sobre `card`, nunca sobre a faixa azul.
 */
const CONTATO_ENCARREGADO = import.meta.env.VITE_CONTATO_ENCARREGADO?.trim();

/**
 * A central de atendimento da cidade. É o único dado de contato que o rodapé
 * afirma por conta própria, e ele se sustenta sozinho: 156 é o canal único da
 * Prefeitura de São Paulo, publicado por ela, não um endereço desta secretaria.
 *
 * O protótipo trazia mais dois — `sme@prefeitura.sp.gov.br` e um endereço na Rua
 * Borges Lagoa. Nenhum dos dois veio: são da Secretaria Municipal de Educação,
 * que é usuária do sistema e não a dona dele, e nenhum consta de documento deste
 * repositório. Decisão do dono em 2026-09-15, junto com a do rodapé em quatro
 * colunas.
 */
const CENTRAL_156 = {
  rotulo: 'Central 156 (SP156)',
  url: 'https://sp156.prefeitura.sp.gov.br',
} as const;

const ESTILO_LINK_RODAPE =
  'text-sidebar-foreground/85 underline-offset-4 hover:text-sidebar-foreground hover:underline';

/**
 * Cabeçalho: só a árvore do protótipo. "Publicações" não é bug nem
 * problema, é funcionalidade real sem equivalente no protótipo — por isso sai
 * daqui, seguindo a regra de fidelidade combinada em 2026-09-06. A tela
 * continua em `/publicacoes`, e o dono decidiu em 2026-09-30 onde ela se
 * alcança: pelo cartão "Publicações vigentes" da página Legislação
 * (`CONSULTAS_POR_PAGINA.legislacao`, PR #190), não pelo cabeçalho. A antiga
 * `/inscricoes` não é mais rota: saiu com a inscrição manual, D-08 — a
 * inscrição é o autocadastro.
 */
const ITENS_DO_CABECALHO: readonly ItemPublico[] = CABECALHO_PUBLICO;

function estaAtivo(item: ItemPublico, pathname: string): boolean {
  if (item.exato) {
    return pathname === item.caminho;
  }
  if (pathname === item.caminho || pathname.startsWith(`${item.caminho}/`)) {
    return true;
  }
  return (item.filhos ?? []).some((filho) => estaAtivo(filho, pathname));
}

const ESTILO_ITEM_ATIVO =
  'rounded-md bg-sidebar-accent px-3 py-1.5 text-sm font-semibold text-sidebar-accent-foreground';
const ESTILO_ITEM =
  'rounded-md px-3 py-1.5 text-sm text-sidebar-foreground/90 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground';

/**
 * Link de um item do cabeçalho, com o destaque e o `aria-current` da página
 * atual. É o mesmo no cabeçalho largo e no menu do celular (só a caixa muda,
 * por `className`), para que "qual item está ativo" seja decidido num lugar só.
 */
function LinkDoCabecalho({
  item,
  pathname,
  className,
  aoNavegar,
}: {
  item: ItemPublico;
  pathname: string;
  className?: string;
  aoNavegar?: () => void;
}) {
  const ativo = estaAtivo(item, pathname);

  return (
    <Link
      to={item.caminho}
      aria-current={ativo ? 'page' : undefined}
      className={cn(ativo ? ESTILO_ITEM_ATIVO : ESTILO_ITEM, className)}
      onClick={aoNavegar}
    >
      {item.titulo}
    </Link>
  );
}

/**
 * O botão de acesso da direita, quando ele é um link: "Ir para a minha área"
 * com sessão do Keycloak, "Entrar" sem sessão nenhuma.
 *
 * A sessão do inscrito não passa por aqui: ela é um menu (`MenuDoInscrito`),
 * não um destino.
 */
function AtalhoDeAcesso({ usuario }: { usuario: UsuarioAutenticado | null }) {
  if (usuario) {
    return (
      <Button asChild size="sm" variant="secondary">
        <Link to={destinoDoUsuario(usuario)}>Ir para a minha área</Link>
      </Button>
    );
  }

  return (
    <Button asChild size="sm" variant="secondary" className="gap-1.5">
      <Link to="/login">
        <LogIn className="h-4 w-4" aria-hidden="true" />
        Entrar
      </Link>
    </Button>
  );
}

/**
 * Consulta que casa com o `lg:` do Tailwind (1024 px): é a largura em que a
 * `<nav>` do cabeçalho aparece e o botão do menu some, como no protótipo
 * (`PortalPublicoLayout.tsx`: `hidden lg:flex` na navegação, `lg:hidden` no
 * botão e no menu).
 */
const CONSULTA_CABECALHO_LARGO = '(min-width: 1024px)';

const ID_DO_MENU_MOVEL = 'menu-do-portal-movel';

/**
 * Menu do cabeçalho abaixo de 1024 px, igual ao do protótipo: um painel que
 * abre embaixo do cabeçalho, na própria página, com a mesma árvore
 * `CABECALHO_PUBLICO` do cabeçalho largo. Sem ele, "Início", "Sobre o TEG",
 * "Para credenciados" e "Transparência" sumiam em 375 px (regra 4 de
 * interface).
 *
 * "Transparência" não é link, como no cabeçalho largo: vira título de grupo
 * (o `<p>` que rotula a sublista), e os filhos vêm recuados, só com o nome,
 * como no protótipo. O painel não repete o "Entrar": o cabeçalho continua
 * visível acima dele.
 *
 * As cores são o par `sidebar` do cabeçalho, os mesmos `ESTILO_ITEM` e
 * `ESTILO_ITEM_ATIVO`, que a auditoria de contraste confere nos temas.
 */
function MenuDoCabecalhoMovel({
  pathname,
  aoNavegar,
}: {
  pathname: string;
  aoNavegar: () => void;
}) {
  return (
    <nav
      id={ID_DO_MENU_MOVEL}
      aria-label="Navegação do portal (menu)"
      className="border-t border-sidebar-border px-4 py-3 lg:hidden"
    >
      <ul className="flex flex-col gap-1">
        {ITENS_DO_CABECALHO.map((item) => {
          if (!item.filhos || item.filhos.length === 0) {
            return (
              <li key={item.caminho}>
                <LinkDoCabecalho
                  item={item}
                  pathname={pathname}
                  className="block py-2"
                  aoNavegar={aoNavegar}
                />
              </li>
            );
          }

          const idDoGrupo = `menu-movel-grupo${item.caminho.replace(/\//g, '-')}`;
          return (
            <li key={item.caminho}>
              <p
                id={idDoGrupo}
                className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/80"
              >
                {item.titulo}
              </p>
              <ul aria-labelledby={idDoGrupo} className="flex flex-col gap-1">
                {item.filhos.map((filho) => (
                  <li key={filho.caminho}>
                    <LinkDoCabecalho
                      item={filho}
                      pathname={pathname}
                      className="block py-2 pl-6"
                      aoNavegar={aoNavegar}
                    />
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export function CascaDoPortal({ children }: { children: React.ReactNode }) {
  const { estado, usuario } = useAuth();
  const { pathname } = useLocation();
  const comContaInterna = estado === 'autenticado' && Boolean(usuario);
  // A sessão do inscrito é um cookie HttpOnly: a tela não o enxerga e pergunta
  // ao servidor. Quem tem sessão do Keycloak não é inscrito nesta aba, e a
  // pergunta nem sai. Erro de rede cai no "Entrar", como sem sessão.
  const sessaoDoInscrito = useSessaoDoInscrito({ habilitada: !comContaInterna });
  const inscrito = comContaInterna ? null : (sessaoDoInscrito.data?.inscrito ?? null);
  const usuarioInterno = comContaInterna ? usuario : null;

  /*
   * O menu do celular guarda o endereço em que foi aberto e só fica aberto
   * enquanto o endereço é esse: navegou, fechou, sem efeito que sincronize duas
   * fontes. O clique no link também fecha, porque tocar no item da página atual
   * não muda o endereço.
   */
  const [menuAbertoEm, setMenuAbertoEm] = useState<string | null>(null);
  const menuAberto = menuAbertoEm === pathname;
  const fecharMenu = () => setMenuAbertoEm(null);

  /*
   * Quem gira o tablet ou alarga a janela com o menu aberto passa para o
   * cabeçalho largo, onde o botão some: o menu fecha junto. O ouvinte só existe
   * enquanto ele está aberto.
   */
  useEffect(() => {
    if (!menuAberto || typeof window.matchMedia !== 'function') {
      return;
    }
    const consulta = window.matchMedia(CONSULTA_CABECALHO_LARGO);
    const aoMudar = (evento: MediaQueryListEvent) => {
      if (evento.matches) {
        setMenuAbertoEm(null);
      }
    };
    consulta.addEventListener('change', aoMudar);
    return () => consulta.removeEventListener('change', aoMudar);
  }, [menuAberto]);

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <SkipToContent />

      <header className="border-b border-border bg-sidebar text-sidebar-foreground">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex items-center gap-3">
            <img src={logoCidade} alt="Cidade de São Paulo" className="h-10 w-auto shrink-0" />
            <span className="h-8 w-px bg-sidebar-border" aria-hidden="true" />
            <Link to="/" className="flex items-center gap-2 rounded-md">
              <TegLogo size="sm" className="bg-sidebar-accent" />
              <span className="min-w-0">
                <span className="block truncate text-sm font-bold leading-tight">Sistema TEG</span>
                <span className="block truncate text-xs text-sidebar-foreground/80">
                  Transporte Escolar Gratuito
                </span>
              </span>
            </Link>
          </div>

          {/*
            Como no protótipo: navegação larga a partir de 1024 px; abaixo,
            tema, atalho de acesso e, por último, o botão do menu.
          */}
          <div className="flex items-center gap-2">
            <nav aria-label="Navegação do portal" className="hidden lg:block">
              <ul className="flex items-center gap-1">
                {ITENS_DO_CABECALHO.map((item) => {
                  const ativo = estaAtivo(item, pathname);

                  if (!item.filhos || item.filhos.length === 0) {
                    return (
                      <li key={item.caminho}>
                        <LinkDoCabecalho item={item} pathname={pathname} />
                      </li>
                    );
                  }

                  /*
                   * Item com submenu não é link — no protótipo, "Transparência"
                   * só abre o menu, sem página própria (`PortalPublicoLayout.tsx`,
                   * `fallbackNav`, nó sem `to`). Só os filhos navegam.
                   */
                  return (
                    <li key={item.caminho}>
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          className={`${ativo ? ESTILO_ITEM_ATIVO : ESTILO_ITEM} inline-flex items-center gap-1`}
                        >
                          {item.titulo}
                          <ChevronDown className="h-4 w-4" aria-hidden="true" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="start"
                          className="w-72 rounded-xl p-2 shadow-xl"
                        >
                          {item.filhos.map((filho) => (
                            <DropdownMenuItem key={filho.caminho} asChild>
                              {/*
                                O DropdownMenuItem (asChild) funde a classe dele
                                ("flex items-center") neste mesmo elemento, sem
                                dedução de conflito — é concatenação simples do
                                Radix Slot. "block!" força o display certo
                                independente da ordem gerada pelo Tailwind.
                              */}
                              <Link
                                to={filho.caminho}
                                className="block! rounded-lg px-3 py-2.5 transition-colors hover:bg-muted"
                              >
                                <span className="text-sm font-medium text-foreground">
                                  {filho.titulo}
                                </span>
                                {filho.descricao && (
                                  <span className="block! text-xs text-muted-foreground mt-0.5">
                                    {filho.descricao}
                                  </span>
                                )}
                              </Link>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <ThemeToggle />

            {inscrito ? (
              <MenuDoInscrito inscrito={inscrito} />
            ) : (
              <AtalhoDeAcesso usuario={usuarioInterno} />
            )}

            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground lg:hidden"
              aria-label={menuAberto ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={menuAberto}
              aria-controls={menuAberto ? ID_DO_MENU_MOVEL : undefined}
              onClick={() => setMenuAbertoEm(menuAberto ? null : pathname)}
            >
              {menuAberto ? (
                <X className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Menu className="h-5 w-5" aria-hidden="true" />
              )}
            </Button>
          </div>
        </div>

        {menuAberto && <MenuDoCabecalhoMovel pathname={pathname} aoNavegar={fecharMenu} />}
      </header>

      <main id="conteudo" className="flex-1">
        {children}
      </main>

      <footer className="border-t border-sidebar-border bg-sidebar text-sidebar-foreground">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
          <div className="grid gap-8 lg:grid-cols-4">
            <div className="space-y-4">
              <img src={logoCidade} alt="Cidade de São Paulo" className="h-11 w-auto" />
              <p className="text-sm leading-relaxed text-sidebar-foreground/85">
                Transporte Escolar Gratuito — Secretaria Municipal de Mobilidade Urbana e Transporte
                de São Paulo. Gestão do credenciamento, da frota, das ordens de serviço e da
                remuneração dos transportadores.
              </p>
            </div>

            {/*
              Um `<nav>` só, com três listas dentro, e não três `<nav>` irmãos: o
              título de cada coluna já nomeia a sua lista para quem usa leitor de
              tela, e um marco de navegação por coluna só multiplicaria o atalho
              sem acrescentar destino.
            */}
            <nav
              aria-label="Links do rodapé"
              className="grid gap-8 sm:grid-cols-2 lg:col-span-3 lg:grid-cols-3"
            >
              {SECOES_DO_RODAPE.map(({ chave, titulo }) => (
                <div key={chave} className="space-y-5">
                  <h2 className="text-sm font-semibold uppercase tracking-wider text-sidebar-foreground">
                    {titulo}
                  </h2>

                  {chave === 'contato' && (
                    <div className="space-y-3 border-b border-sidebar-border pb-5">
                      {/*
                        Só o canal, sem o parágrafo de direitos que estava aqui.
                        `docs/LGPD-E-ACESSIBILIDADE.md`, seção 5, exige do rodapé
                        uma coisa: "contato do encarregado publicado no rodapé do
                        portal". A lista de direitos é da seção 4 e mora no aviso
                        de privacidade — que está linkado três linhas abaixo.
                        Repeti-la aqui dava a esta coluna o dobro da altura das
                        outras, e o protótipo não a tem.
                      */}
                      <p className="flex items-start gap-2 text-sm">
                        <Phone
                          className="mt-0.5 h-4 w-4 shrink-0 text-sidebar-foreground/70"
                          aria-hidden="true"
                        />
                        <a
                          href={CENTRAL_156.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={ESTILO_LINK_RODAPE}
                        >
                          {CENTRAL_156.rotulo}
                        </a>
                      </p>

                      {CONTATO_ENCARREGADO ? (
                        <p className="flex items-start gap-2 text-sm">
                          <Mail
                            className="mt-0.5 h-4 w-4 shrink-0 text-sidebar-foreground/70"
                            aria-hidden="true"
                          />
                          <a href={`mailto:${CONTATO_ENCARREGADO}`} className={ESTILO_LINK_RODAPE}>
                            {CONTATO_ENCARREGADO}
                          </a>
                        </p>
                      ) : (
                        <p className="flex items-start gap-2 text-sm leading-relaxed text-sidebar-foreground/85">
                          <Mail
                            className="mt-0.5 h-4 w-4 shrink-0 text-sidebar-foreground/70"
                            aria-hidden="true"
                          />
                          <span>
                            Canal do encarregado de proteção de dados publicado pela Secretaria
                          </span>
                        </p>
                      )}
                    </div>
                  )}

                  <ul className="space-y-3 text-sm">
                    {RODAPE_PUBLICO.filter((item) => item.secao === chave).map((item) => (
                      <li key={`${item.caminho}-${item.titulo}`}>
                        <Link to={item.caminho} className={ESTILO_LINK_RODAPE}>
                          {item.titulo}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          </div>

          {/*
            Barra de baixo em DUAS linhas, como a do protótipo: uma linha de
            selo/afirmação e, abaixo dela, o copyright sozinho.

            À esquerda, onde o protótipo põe o Selo CPA, vai "Acesso ao sistema".
            O selo continua fora — é certificação concedida pela Comissão
            Permanente de Acessibilidade, que ninguém emitiu, e `App.teste.tsx`
            falha se a string voltar. O link de entrada também não existe no
            rodapé do protótipo, mas precisa de um lugar fora das três colunas:
            entre os nove institucionais ele fazia a coluna de Serviços parecer
            ter um item que a conferência de conformidade não conhece.

            À direita, onde o protótipo escreve "Desenvolvido em conformidade com
            a LGPD", fica a afirmação que este sistema pode sustentar hoje: que o
            tratamento SEGUE a lei. Conformidade declarada é juízo sobre o
            próprio trabalho, e a jornada de consentimento (E5.4) ainda não
            fechou — é o mesmo motivo do Selo CPA, em grau menor.
          */}
          <div className="mt-12 space-y-4 border-t border-sidebar-border pt-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Link to="/login" className={`text-sm ${ESTILO_LINK_RODAPE}`}>
                Acesso ao sistema
              </Link>
              <p className="text-xs text-sidebar-foreground/70">
                O tratamento de dados pessoais segue a Lei nº 13.709/2018 (LGPD).
              </p>
            </div>

            {/*
              `pr-16` reserva a quadra inferior direita desta linha, a última do
              documento: o widget de acessibilidade é `fixed bottom-4 right-4`
              com 48 px de lado e cobre o que estiver ali.
            */}
            <p className="pr-16 text-xs leading-relaxed text-sidebar-foreground/70">
              © {new Date().getFullYear()} Sistema TEG — Transporte Escolar Gratuito. Prefeitura de
              São Paulo, Secretaria Municipal de Mobilidade Urbana e Transporte.
            </p>
          </div>
        </div>
      </footer>

      <CookieConsent />
      <AcessibilidadeWidget />
    </div>
  );
}
